import type { Router } from 'vue-router';

/**
 * Resilience for lazy-route chunk loading after a deploy.
 *
 * Every route is a lazy `() => import('../views/…')`, so navigation pulls a
 * content-hashed chunk. Production deploys run `aws s3 sync --delete`
 * (scripts/publish.sh), which immediately removes the previous build's chunks.
 * A client still holding the old `index.html` (cached, or an open tab) then
 * requests a hash that no longer exists → S3/CloudFront serves `index.html`
 * (text/html) or 404 → the dynamic import throws. Observed in Sentry as
 * CUSTOMER-PORTAL-2/5/7/8.
 *
 * Recovery: force a one-time full reload so the browser fetches the fresh
 * `index.html` + current chunk hashes. A sessionStorage flag prevents an
 * infinite reload loop when the asset is genuinely unreachable (real outage),
 * and is cleared on the next successful navigation so a later deploy in the
 * same long-lived session can still recover.
 */

export const RELOAD_FLAG = 'sera:chunk-reload';

/**
 * True when an error is a dynamic-import / asset-preload failure (stale chunk),
 * as opposed to an ordinary navigation/runtime error we must NOT swallow.
 * Matches the message variants browsers emit across engines + the Vite preload
 * helper (see Sentry CUSTOMER-PORTAL-2/5/7/8).
 *
 * Also matches vue-router's `Couldn't resolve component "…" at "…"` message
 * (Sentry CUSTOMER-PORTAL-M): when the stale `index.html` served back for a
 * missing chunk parses as an *empty JS module* (no `default` export), the
 * dynamic import resolves but vue-router can't find the lazy component's
 * default export and throws — the same stale-chunk deploy artifact, recovered
 * by the same one-time reload.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return (
    /failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /importing a module script failed/i.test(message) ||
    /is not a valid javascript mime type/i.test(message) ||
    /unable to preload css/i.test(message) ||
    /couldn't resolve component/i.test(message)
  );
}

export interface ReloadDeps {
  /** Read the loop-guard flag (null when unset). */
  getFlag: () => string | null;
  /** Set the loop-guard flag. */
  setFlag: () => void;
  /** Clear the loop-guard flag. */
  clearFlag: () => void;
  /** Full document navigation: to `path` when given, else reload current URL. */
  navigate: (path?: string) => void;
}

/**
 * Reload at most once until the next successful navigation. Returns true when a
 * reload was triggered, false when suppressed by the loop guard.
 */
export function reloadForChunkErrorOnce(
  deps: ReloadDeps,
  targetPath?: string,
): boolean {
  if (deps.getFlag()) return false; // already attempted — avoid a reload loop
  deps.setFlag();
  deps.navigate(targetPath);
  return true;
}

/**
 * Wire chunk-load recovery into a router:
 * - `onError`: reload to the target when navigation fails on a chunk-load error.
 * - successful navigation clears the loop guard.
 * - `vite:preloadError`: preventDefault (suppress the unhandled throw) + reload.
 */
export function installChunkReloadHandlers(
  router: Router,
  deps: ReloadDeps,
): void {
  router.onError((error, to) => {
    if (isChunkLoadError(error)) {
      reloadForChunkErrorOnce(deps, to?.fullPath);
    }
  });

  router.afterEach(() => {
    deps.clearFlag();
  });

  if (typeof window !== 'undefined') {
    window.addEventListener('vite:preloadError', ((event: Event) => {
      // Recover via reload instead of letting Vite throw the preload error.
      event.preventDefault();
      reloadForChunkErrorOnce(deps);
    }) as EventListener);
  }
}
